import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Bus, ArrowRight, Loader2, School, KeyRound, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "parent" });
  const [showPwd, setShowPwd] = useState(false);
  const [tenantMode, setTenantMode] = useState("join"); // join | create | demo
  const [tenantCode, setTenantCode] = useState("");
  const [newTenantName, setNewTenantName] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = { ...form };
    if (tenantMode === "join") payload.tenant_code = tenantCode.trim().toUpperCase();
    else if (tenantMode === "create") payload.new_tenant_name = newTenantName.trim();
    // demo => omit, backend joins JULES default
    const res = await register(payload);
    setLoading(false);
    if (res.ok) {
      toast.success(`Compte créé · ${res.user.tenant?.name || ""}`);
      const target = res.user.role === "admin" ? "/admin" : res.user.role === "driver" ? "/driver" : "/parent";
      nav(target, { replace: true });
    } else toast.error(res.error);
  };

  // create-school option requires admin role
  const isCreateInvalid = tenantMode === "create" && form.role !== "admin";

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white">
      <div className="p-8 lg:p-12 flex flex-col overflow-y-auto">
        <Link to="/" className="flex items-center gap-2 mb-8" data-testid="register-logo">
          <div className="w-8 h-8 bg-[#FFC60A] border border-zinc-900 flex items-center justify-center">
            <Bus size={18} />
          </div>
          <span className="font-display font-black text-xl tracking-tighter">LOGIBUS</span>
        </Link>

        <div className="max-w-sm">
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Inscription</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2">Créer un compte.</h1>

          <form onSubmit={onSubmit} className="mt-6 space-y-3" data-testid="register-form">
            <div>
              <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Nom complet</label>
              <input data-testid="register-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 w-full border border-zinc-900 px-3 py-2.5 focus:ring-2 focus:ring-zinc-900 focus:outline-none bg-white" />
            </div>
            <div>
              <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Email</label>
              <input data-testid="register-email-input" type="email" name="email" autoComplete="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1 w-full border border-zinc-900 px-3 py-2.5 focus:ring-2 focus:ring-zinc-900 focus:outline-none bg-white" />
            </div>
            <div>
              <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Mot de passe</label>
              <div className="relative mt-1">
                <input data-testid="register-password-input" type={showPwd ? "text" : "password"} name="password" autoComplete="new-password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full border border-zinc-900 px-3 py-2.5 pr-11 focus:ring-2 focus:ring-zinc-900 focus:outline-none bg-white" />
                <button type="button" data-testid="register-password-toggle"
                  onClick={() => setShowPwd((v) => !v)}
                  title={showPwd ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  className="absolute right-0 top-0 h-full px-3 text-zinc-500 hover:text-zinc-900">
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Rôle</label>
              <div className="mt-1 grid grid-cols-3 gap-0 border border-zinc-900">
                {["admin", "driver", "parent"].map((r) => (
                  <button type="button" key={r} data-testid={`register-role-${r}`}
                    onClick={() => setForm({ ...form, role: r })}
                    className={`py-2 text-xs font-bold uppercase tracking-widest border-r border-zinc-900 last:border-r-0 ${form.role === r ? "bg-[#FFC60A]" : "bg-white hover:bg-zinc-100"}`}>
                    {r === "admin" ? "Admin" : r === "driver" ? "Chauffeur" : "Parent"}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">École</label>
              <div className="mt-1 grid grid-cols-3 gap-0 border border-zinc-900">
                <button type="button" data-testid="tenant-mode-join" onClick={() => setTenantMode("join")}
                  className={`py-2 text-xs font-bold uppercase tracking-widest border-r border-zinc-900 ${tenantMode === "join" ? "bg-zinc-900 text-white" : "bg-white"}`}>
                  Rejoindre
                </button>
                <button type="button" data-testid="tenant-mode-create" onClick={() => setTenantMode("create")}
                  className={`py-2 text-xs font-bold uppercase tracking-widest border-r border-zinc-900 ${tenantMode === "create" ? "bg-zinc-900 text-white" : "bg-white"}`}>
                  Créer
                </button>
                <button type="button" data-testid="tenant-mode-demo" onClick={() => setTenantMode("demo")}
                  className={`py-2 text-xs font-bold uppercase tracking-widest ${tenantMode === "demo" ? "bg-zinc-900 text-white" : "bg-white"}`}>
                  Démo
                </button>
              </div>

              {tenantMode === "join" && (
                <div className="mt-2">
                  <div className="flex gap-2">
                    <KeyRound size={16} className="mt-3 text-zinc-500" />
                    <input data-testid="register-tenant-code" required value={tenantCode} onChange={(e) => setTenantCode(e.target.value)} placeholder="Code école (ex: JULES)" maxLength={20}
                      className="flex-1 border border-zinc-900 px-3 py-2.5 font-mono uppercase tracking-widest focus:ring-2 focus:ring-zinc-900 focus:outline-none bg-white" />
                  </div>
                  <div className="text-[10px] font-mono text-zinc-500 mt-1">Demandez le code à l'admin de votre école.</div>
                </div>
              )}
              {tenantMode === "create" && (
                <div className="mt-2">
                  <div className="flex gap-2">
                    <School size={16} className="mt-3 text-zinc-500" />
                    <input data-testid="register-tenant-new-name" required={tenantMode === "create"} value={newTenantName} onChange={(e) => setNewTenantName(e.target.value)} placeholder="Nom de l'école"
                      className="flex-1 border border-zinc-900 px-3 py-2.5 focus:ring-2 focus:ring-zinc-900 focus:outline-none bg-white" />
                  </div>
                  <div className={`text-[10px] font-mono mt-1 ${isCreateInvalid ? "text-red-600" : "text-zinc-500"}`}>
                    {isCreateInvalid ? "Réservé au rôle Admin." : "Vous devenez admin de cette nouvelle école. Un code sera généré."}
                  </div>
                </div>
              )}
              {tenantMode === "demo" && (
                <div className="mt-2 border border-zinc-900 bg-[#FFF7D6] p-2 text-xs">
                  Vous rejoignez l'organisation par défaut <b>JULES</b>. Vous pourrez ajouter vos écoles dans les paramètres.
                </div>
              )}
            </div>

            <button data-testid="register-submit-button" disabled={loading || isCreateInvalid}
              className="w-full bg-[#FFC60A] border border-zinc-900 py-3 font-bold hover:bg-[#ffd43a] inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed">
              {loading ? <Loader2 className="animate-spin" size={16} /> : <>Créer le compte <ArrowRight size={16} /></>}
            </button>
          </form>

          <p className="mt-4 text-sm text-zinc-600">
            Déjà un compte ? <Link data-testid="register-to-login" to="/login" className="font-bold underline">Se connecter</Link>
          </p>
        </div>
      </div>

      <div className="hidden md:block relative border-l border-zinc-900 bg-[#FFC60A]">
        <img src="https://images.unsplash.com/photo-1758782213616-7b4cd41eff29?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85" alt="enfant école" className="w-full h-full object-cover mix-blend-multiply" />
        <div className="absolute bottom-8 left-8 right-8">
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase">Sérénité · Sécurité · Multi-école</div>
          <p className="font-display font-black text-4xl tracking-tighter mt-2">Une plateforme. Toutes vos écoles.</p>
        </div>
      </div>
    </div>
  );
}
